/**
 * MODULE: useAssistantQuotaStatus.ts
 * PURPOSE: Poll workspace AI quota for proactive assistant threshold banners
 */
import { useQuery } from '@tanstack/react-query';
import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';

export type AssistantQuotaStatus = 'ok' | 'warning' | 'critical' | 'exceeded';

export type AssistantQuotaStatusResponse = {
  ai_tokens: {
    limit: number;
    used: number;
    remaining: number | null;
    percent_used: number;
    status: AssistantQuotaStatus;
    active_alert_threshold: number | null;
    reset_at: string | null;
  };
};

const STATUS_URL = `${API_CONFIG.baseURL}/api/v1/platform/workspace/quota/assistant-status`;

async function fetchAssistantQuotaStatus(): Promise<AssistantQuotaStatusResponse> {
  const res = await fetch(STATUS_URL, { headers: getAuthHeaders() });
  if (res.status === 403 || res.status === 401) {
    return {
      ai_tokens: {
        limit: -1,
        used: 0,
        remaining: null,
        percent_used: 0,
        status: 'ok',
        active_alert_threshold: null,
        reset_at: null,
      },
    };
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json() as Promise<AssistantQuotaStatusResponse>;
}

export function useAssistantQuotaStatus(enabled = true) {
  return useQuery({
    queryKey: ['assistant-quota-status'],
    queryFn: fetchAssistantQuotaStatus,
    enabled,
    staleTime: 45_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}
